import React, { ReactNode, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { useHistory, Link } from 'react-router-dom'
import { Typography, Box, makeStyles, Grid } from 'transfix-ui/core'
import { brandColors } from 'transfix-ui/theme/colors'
import { clearUser } from 'utils/actions/user'
import { useChatWidgetContext } from 'utils/context/ChatWidgetContext'
import { useFeatureFlag } from 'utils/context/FeatureFlagsContext'
import { useUserDetails } from 'utils/hooks/useUserDetails'
import { OnboardingCarrierList } from 'apps/fleet-planner/components/carrier-search'
import { ReactComponent as TruckLocked } from 'apps/fleet-planner/components/illustrations/onboarding/TruckLocked.svg'
import { ReactComponent as TruckWarning } from 'apps/fleet-planner/components/illustrations/onboarding/TruckWarning.svg'
import { FP_SUPPORT_EMAIL } from 'apps/fleet-planner/config'
import { getRoute, IOnboardingQueryParams } from 'apps/fleet-planner/constants'
import { useOnboardingWizardContext, useUserContext } from 'apps/fleet-planner/context'
import { useFMSAnalytics } from 'apps/fleet-planner/hooks'
import { Button } from 'transfix-ui'
import { OnboardingStepContainer } from './OnboardingStepContainer'

const useOnboardingStepStyles = makeStyles({
  subtitle: {
    color: brandColors.coolGray5,
  },
  svgContainer: {
    position: 'relative',
    height: 0,
    width: '100%',
    paddingBottom: '80.0643087%',

    '& > svg': {
      position: 'absolute',
      top: 0,
      right: 0,
      left: 0,
      bottom: 0,
      width: '100%',
      height: '100%',
    },
  },
})

/**
 * Step to display when a user has been created but they only selected
 * carriers with users
 */
export function OnboardingStepCarriersApproval() {
  const { trackEvent } = useFMSAnalytics()
  const isFmsRedirectUsersAdminApprovalEnabled = useFeatureFlag('fmsRedirectUsersAdminApproval')
  const history = useHistory()
  const { clearUserData } = useUserContext()
  const { logout } = useUserDetails()
  const classes = useOnboardingStepStyles()
  const { carriers = [], mobileApp } = useOnboardingWizardContext()

  useEffect(() => {
    if (mobileApp) {
      ReactNativeWebView.postMessage('CARRIERS_REQUEST_SUBMITTED')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    trackEvent('Onboarding', 'VIEWED_FMS_ONBOARDING_CARRIER_APPROVAL')
  }, [trackEvent])

  const addCarrierHandler = () => {
    const params: IOnboardingQueryParams = {}
    if (mobileApp) {
      params.mobileApp = 1
    }
    history.push(getRoute('carriers', params))
  }

  const getApprovalSubtitle = (): ReactNode => {
    const count = carriers?.length || 0
    return count
      ? `You asked for access to ${count} carrier${count > 1 ? 's' : ''}`
      : 'Tell us which company you work for to start booking loads and managing your fleet on Transfix.'
  }

  const getApprovalCaption = (): ReactNode => {
    return carriers.length ? (
      <>
        You’ll get an email letting you know if you’ve been approved to manage each carrier on
        Transfix. An email was sent to the carrier’s Admin to approve you.{' '}
        <a href={`mailto:${FP_SUPPORT_EMAIL}`}>Contact us</a> if you need help.
      </>
    ) : (
      ''
    )
  }

  const getApprovalIllustration = (): ReactNode => {
    return carriers.length ? <TruckLocked /> : <TruckWarning />
  }

  const dispatch = useDispatch()
  const { shutdown } = useChatWidgetContext()

  const logoutHandler = async () => {
    if (mobileApp) {
      ReactNativeWebView.postMessage('LOG_OUT')
    }

    if (isFmsRedirectUsersAdminApprovalEnabled) {
      dispatch(clearUser)

      await clearUserData()

      shutdown(true)

      logout({ origin: 'fms' })
    }
  }

  return (
    <OnboardingStepContainer textAlign='left' title='You aren’t managing any carriers yet'>
      <Grid container spacing={3}>
        <Grid item xs={12} md={9}>
          <Box pb={2}>
            <Box mb={0.5}>
              <Typography variant='body1' component='div'>
                {getApprovalSubtitle()}
              </Typography>
            </Box>
            <Typography variant='caption' component='div' className={classes.subtitle}>
              {getApprovalCaption()}
            </Typography>
          </Box>
          {!!carriers.length && (
            <Box pb={2}>
              <OnboardingCarrierList carriers={carriers} isMessageVisible={false} />
            </Box>
          )}
          <Box pb={2}>
            <Button onClick={addCarrierHandler} color='secondary' data-test='add-carrier'>
              Add another carrier
            </Button>
          </Box>
          <Box onClick={logoutHandler}>
            <Link to={getRoute('index')}>Log Out</Link>
          </Box>
        </Grid>
        <Grid item xs={12} md={3}>
          <Box className={classes.svgContainer}>{getApprovalIllustration()}</Box>
        </Grid>
      </Grid>
    </OnboardingStepContainer>
  )
}
